import { useContext, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { cloneDeep } from 'es-toolkit';

import {
  AdornedInput,
  FormField,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Popover,
  Text,
  TextButton,
  TriggerText,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertyFloorPlanEditWebApiHandler_price } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../../../actions/PropertiesActions';
import wizardActions from '../../../../components/Wizard/actions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm } from '../../../../hooks';
import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import accessUtils from '../../../../utils/accessUtils';
import { postingConstraints } from '../../../../utils/formValidation';
import numberUtils from '../../../../utils/numberUtils';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import { GET_LISTING_RECS } from '../../../../constants/RecommendationsConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import WizardServerError from '../../../../model/WizardServerError';

import InputFieldWarningConfirmationSFModal from '../../../../components/Modal/InputFieldWarningConfirmationSFModal';
import { WizardContext } from '../../../../components/Wizard/context';
import WizardAlert from '../../../../components/WizardAlert';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import type { ChangeEvent } from 'react';

const inputName = 'price';

const priceWarningLimit = 50000;

const rentZestimateLink = 'https://www.zillow.com/z/rental/what-is-a-rent-zestimate/';

const mockEvent = {
  target: {
    name: inputName,
    value: null,
  },
};

type Props = {
  isRelistingFieldset?: boolean;
};

const MonthlyRentFormField = ({ isRelistingFieldset = false }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const { mutateAsync: updatePrice } = useRentalManagerUserPropertyFloorPlanEditWebApiHandler_price();

  const { cookieEnabledServices, floorplans, isTotalMonthlyPriceWithFeeEligible, rentZestimate } = useSelector(
    (state) => ({
      cookieEnabledServices: state.app.cookieEnabledServices,
      floorplans: state.properties.currentProperty.floorplans,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      rentZestimate: state.properties.currentProperty.rentZestimate,
    }),
    shallowEqual,
  );
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));
  const {
    modularListingState: { serverErrorObj, shouldSaveOnBlur, isInModalState, isSaveComplete },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const previousValue = useRef();
  const priceInputRef = useRef<HTMLInputElement>(null);

  const [showWarningConfirmationModal, setShowWarningConfirmationModal] = useState(false);

  const floorplan = floorplans[0] || {};
  const { price, floorPlanId } = floorplan;

  const {
    inputs,
    errorObj,
    hasFieldError: hasFieldErrorCheck,
    handleChange: handleFormChange,
  } = useForm(
    {
      price,
    },
    postingConstraints,
  );

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  useEffect(() => {
    // @ts-expect-error
    previousValue.current = inputs[inputName];
  }, []);

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          // @ts-expect-error
          value: inputs[inputName],
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = async (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldErrorCheck(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    const numCastValue = Number(value || 0);
    const numCastPrevValue = Number(previousValue.current || 0);

    if (numCastValue === numCastPrevValue && !isInModalState) return;

    if (numCastValue !== numCastPrevValue && numCastValue >= priceWarningLimit && !showWarningConfirmationModal) {
      // open confirmation modal
      setShowWarningConfirmationModal(true);
      return;
    }

    // @ts-expect-error
    previousValue.current = inputs[inputName];

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    try {
      if (!floorPlanId) {
        logError({
          errorType: 'FloorPlanIdMissingError',
          error: new Error('Missing floorPlanId'),
          context: `MonthlyRentFormField.save: fieldName: ${fieldName}, floorplans: ${JSON.stringify(floorplans)}`,
        });
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }

      await updatePrice({
        getParams: {
          floorPlanId,
          price: value,
        },
      });

      const copyFloorplan = cloneDeep(floorplans);
      copyFloorplan[0][fieldName] = Number(value);
      queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

      dispatch(PropertiesActions.updateCurrentProperty({ floorplans: copyFloorplan }));
      dispatch(PropertiesActions.updateCurrentProperty({ price: Number(value) }));

      // reset save cta
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      updateServerError(errorMessage, fieldName);
    } finally {
      wizardActions.updateIsFetchingData({
        reducerWizardDispatch,
        isFetchingData: false,
      });
    }
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  // confirmation modal related functions
  const handleModalClose = () => {
    setShowWarningConfirmationModal(false);
    // @ts-expect-error
    mockEvent.target.value = previousValue.current;

    handleFormChange(mockEvent);
    // @ts-expect-error
    priceInputRef.current.focus();
  };

  const handleModalSubmit = () => {
    if (isInModalState) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: true });

      return;
    }

    // @ts-expect-error
    mockEvent.target.value = inputs[inputName];
    // @ts-expect-error
    save(mockEvent);

    setShowWarningConfirmationModal(false);
  };

  const renderZestimate = () => {
    const rentZestimateCurrency = numberUtils.asCurrency(Number(rentZestimate));
    return (
      <Popover
        body={
          <Text>
            The Rent Zestimate is Zillow&apos;s best estimate of a home&apos;s value as a rental property.{' '}
            <TextButton asChild>
              <a href={rentZestimateLink} rel="noreferrer" target="_blank">
                Learn more
              </a>
            </TextButton>
            {' about the Rent Zestimate.'}
          </Text>
        }
        header={<Heading level={4}>Rent Zestimate®</Heading>}
        trigger={<TriggerText css={{ fontWeight: 'regular' }}>Rent Zestimate®: {rentZestimateCurrency}</TriggerText>}
      />
    );
  };

  return (
    <div data-test="MonthlyRentFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      {!!rentZestimate && !isRelistingFieldset && (
        <Paragraph
          css={{ marginBottom: '600' }}
        >{`The current Rent Zestimate for this property is: ${numberUtils.asCurrency(rentZestimate)}`}</Paragraph>
      )}
      <FormField
        label={
          <Label required={true}>
            {canAccessTotalMonthlyPriceWithFee ? 'Base' : 'Monthly'} rent &nbsp;
            {isRelistingFieldset && !!rentZestimate && renderZestimate()}
          </Label>
        }
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                ref={priceInputRef}
                name={inputName}
                value={String(inputs[inputName] || '')}
                onChange={handleChange}
                onBlur={save}
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
            endAdornment={<AdornedInput.Adornment>/month</AdornedInput.Adornment>}
          />
        }
        // @ts-expect-error
        error={!!errorObj.price}
        // @ts-expect-error
        description={<FormHelp error={!!errorObj.price}>{errorObj.price}</FormHelp>}
      />

      {showWarningConfirmationModal && (
        <InputFieldWarningConfirmationSFModal
          fieldName={inputName}
          // @ts-expect-error
          previousValue={previousValue.current}
          // @ts-expect-error
          value={inputs[inputName]}
          isModularListing={true}
          handleModalCloseML={handleModalClose}
          handleModalSubmitML={handleModalSubmit}
        />
      )}
    </div>
  );
};

export default MonthlyRentFormField;
